import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { combineLatest, of, BehaviorSubject, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { SessionStorage } from 'ngx-webstorage';
import { isBefore, isWithinInterval } from 'date-fns';

import { AwardProgramInfo } from 'services/awards/awards.service';
import { ConfigService } from 'services/config/config.service';

@Injectable({
    providedIn: 'root',
})

export class ApplicationService {
    nomByLocationSubject: BehaviorSubject<SchoolNominationInfo> = new BehaviorSubject<SchoolNominationInfo>(null);

    public get NomByLocation(): SchoolNominationInfo {
        return this.nomByLocationSubject.value;
    }

    public set NomByLocation(value: SchoolNominationInfo) {
        this.nomByLocationSubject.next(value);
    }

    public get AvailableAwardPrograms() {
        return this._AvailableAwardPrograms;
    }

    @SessionStorage() _AllSchoolGroups: SchoolGroupInfo[];
    @SessionStorage() _AllSchools: SchoolInfo[];
    @SessionStorage() _AllSchoolYears: string[];
    @SessionStorage() _AvailableAwardPrograms: AwardProgramInfo[];
    @SessionStorage() _AvailableFormTemplates: FormTemplateInfo[];
    @SessionStorage() _AvailableNomineeTypes: NomineeTypeInfo[];
    @SessionStorage() _AvailableSchools: SchoolInfo[];
    @SessionStorage() _CurrentSchoolYear: string;
    @SessionStorage() _NominationsBySchool: SchoolNominationInfo;
    @SessionStorage() _Judges: JudgeInfo[];
    @SessionStorage() _LoadedAwardID: number;
    @SessionStorage() _LoadedSchoolYear: string;
    @SessionStorage() _NominationWindows: NominationWindowInfo[];
    @SessionStorage() _WindowOpenInfo: WindowOpenInfo;
    @SessionStorage() _WindowTypes: WindowTypeInfo[];

    @SessionStorage() _TOYFormWindowOpen: boolean;
    @SessionStorage() _TOYPhotoWindowOpen: boolean;
    @SessionStorage() _TOYVideoWindowOpen: boolean;
    @SessionStorage() _TOYJudgeInitialWindowOpen: boolean;
    @SessionStorage() _TOYJudgeInitialTieBreakerWindowOpen: boolean;
    @SessionStorage() _TOYJudgeFinalWindowOpen: boolean;
    @SessionStorage() _TOYJudgeFinalTieBreakerWindowOpen: boolean;
    @SessionStorage() _SREFormWindowOpen: boolean;
    @SessionStorage() _SREPhotoWindowOpen: boolean;
    @SessionStorage() _SREVideoWindowOpen: boolean;
    @SessionStorage() _SREJudgeInitialWindowOpen: boolean;
    @SessionStorage() _SREJudgeInitialTieBreakerWindowOpen: boolean;
    @SessionStorage() _SREJudgeFinalWindowOpen: boolean;
    @SessionStorage() _SREJudgeFinalTieBreakerWindowOpen: boolean;

    constructor(
        private http: HttpClient,
        private configService: ConfigService
    ) {
        // Anything with dates must be reparsed in case this is a page refresh retrieving from session variables

        if (this._NominationsBySchool != null) this.nomByLocationSubject.next(this._NominationsBySchool);
    }

    // #region Add Designee Service Call

    AddDesignee(
        AuthToken: string,
        CurrentRole: string,
        AwardID: number,
        UserID: number,
        SAPID: string,
        PositionNo: string,
        LocationNumber: string,
        ExpirationDate: Date,
		Rights: number[],
		SchoolYear: string
    ) {
        return this.http.post<DesigneeInfo[]>(
            this.configService.hostAddress + `Api/Services/App/AddDesignee`,
            {
                AuthToken: AuthToken || '',
                CurrentRole: CurrentRole || '',
                AwardID: AwardID || '',
                UserID: UserID || '',
                SAPID: SAPID || '',
                PositionNo: PositionNo || '',
                LocationNumber: LocationNumber || '',
                ExpirationDate: ExpirationDate || '',
				Rights: Rights || '',
				SchoolYear: SchoolYear
            }
        );
    }

    // #endregion

    // #region Add New User Service Call

    AddNewUser(
        AuthToken: string,
        CurrentRole: string,
        FirstName: string,
        LastName: string,
        Email: string
    ) {
        return this.http.post<NewUserInfo>(
            this.configService.hostAddress + `Api/Services/App/AddNewUser`,
            {
                AuthToken: AuthToken || '',
                CurrentRole: CurrentRole || '',
                FirstName: FirstName || '',
                LastName: LastName || '',
                Email: Email || '',
            }
        );
    }

    // #endregion

    // #region Add School Group Service Call

    AddSchoolGroup(
        AuthToken: string,
        CurrentRole: string,
        AwardID: number,
		dbPacket: any,
		SchoolYear: string
    ) {
        return this.http.post<SchoolGroupInfo[]>(
            this.configService.hostAddress + `Api/Services/App/AddSchoolGroup`,
            {
                AuthToken: AuthToken || '',
                CurrentRole: CurrentRole || '',
                AwardID: AwardID || '',
				SchoolGroup: dbPacket,
				SchoolYear: SchoolYear
            }
        );
    }

    // #endregion

    // #region Add Window Service Call

    AddWindow(
        AuthToken: string,
        CurrentRole: string,
        AwardID: number,
        SchoolYear: string,
        NomineeTypeID: number,
        WindowTypeID: number,
        FormTemplateID: number,
        DateBegin: Date,
        DateEnd: Date
    ) {
        return this.http
            .get<NominationWindowInfo[]>(
                this.configService.hostAddress +
                    `Api/Services/App/AddWindow?` +
                    `AuthToken=${AuthToken || ''}&CurrentRole=${CurrentRole || ''}&AwardID=${AwardID || ''}&SchoolYear=${SchoolYear || ''}` +
                    `&NomineeTypeID=${NomineeTypeID || ''}&WindowTypeID=${WindowTypeID || ''}&FormTemplateID=${FormTemplateID || ''}` +
                    `&DateBegin=${DateBegin ? DateBegin.toJSON() : ''}&DateEnd=${DateEnd ? DateEnd.toJSON() : ''}`
            )
            .pipe(
                map(windows => {
                    if (windows && !!windows.length) {
                        // Convert everything to dates for later comparison.
                        windows.forEach(win => {
                            win.DateBegin = win.DateBegin
                                ? new Date(win.DateBegin)
                                : null;
                            win.DateEnd = win.DateEnd
                                ? new Date(win.DateEnd)
                                : null;
                        });
                    }

                    return windows;
                })
            );
    }

    // #endregion

    // #region Delete File Service Call

    DeleteFile(
        AuthToken: string,
        AttachmentID: number,
        FilePath: string,
        AttachmentTypeID: number
    ) {
        return this.http.get<boolean>(
            this.configService.hostAddress +
                `Api/Services/App/DeleteFile?` +
                `AuthToken=${AuthToken || ''}&AttachmentID=${AttachmentID ||
                    ''}&FilePath=${FilePath || ''}` +
                `&AttachmentTypeID=${AttachmentTypeID || ''}`
        );
    }

    // #endregion

    // #region Dev Judging Reset Service Call

    DevJudgingReset(
        AuthToken: string,
        CurrentRole: string,
        AwardID: number,
        NomineeTypeID: number,
		ProcessNumber: number,
		SchoolYear: string
    ) {
        return this.http.get<boolean>(
            this.configService.hostAddress +
            `Api/Services/App/DevJudgingReset?` +
            `AuthToken=${AuthToken || ''}` +
            `&CurrentRole=${CurrentRole || ''}` +
            `&AwardID=${AwardID || ''}` +
            `&NomineeTypeID=${NomineeTypeID || ''}` +
			`&ProcessNumber=${ProcessNumber || ''}` +
			`&SchoolYear=${SchoolYear}`
        );
    }

    // #endregion

    // #region Divide Judges Service Call

    DivideJudges(
        AuthToken: string,
        CurrentRole: string,
		Data: JudgePoolTable[],
		SchoolYear: string
    ) {
        return this.http.post<any>(this.configService.hostAddress + 'Api/Services/App/DivideJudges',
            {
                AuthToken: AuthToken || '',
                CurrentRole: CurrentRole || '',
				Data: Data || '',
				SchoolYear: SchoolYear
            }
        );
    }

    // #endregion

    // #region Email Judges Service Call

    EmailJudges(
        AuthToken: string,
        CurrentRole: string,
        AwardID: number,
        NomineeTypeID: number,
		JudgingPhase: number,
		SchoolYear: string
    ) {
        return this.http.get<any>(
            this.configService.hostAddress + `Api/Services/App/EmailJudgesByPhase?` +
            `AuthToken=${AuthToken || ''}&` +
            `CurrentRole=${CurrentRole || ''}&` +
            `AwardID=${AwardID || ''}&` +
            `NomineeTypeID=${NomineeTypeID || ''}&` +
			`JudgingPhase=${JudgingPhase || ''}` +
			`&SchoolYear=${SchoolYear}`
		);
    }

    // #endregion

    // #region Email Judging Key Service Call

    EmailJudgingKey(
		EmailAddress: string, SchoolYear: string
    ) {
        return this.http.get<any>(
            this.configService.hostAddress + `Api/Services/App/EmailJudgingKey?Email=${EmailAddress}&SchoolYear=${SchoolYear}`);
    }

    // #endregion

    // #region Finalize Judge Nomination Group Service Call

    FinalizeJudgeNominationGroup(
        AuthToken: string,
        CurrentRole: string,
        JudgeID: number
    ) {
        return this.http.get<JudgeNominationInfo>(
            this.configService.hostAddress +
                `Api/Services/App/FinalizeJudgeNominationGroup?` +
                `AuthToken=${AuthToken || ''}&CurrentRole=${CurrentRole ||
                    ''}&JudgeID=${JudgeID || ''}`
        );
    }

    // #endregion

    // #region Finalize Judge Nomination Tie Breaker Group Service Call

    FinalizeJudgeNominationTieBreakerGroup(
        AuthToken: string,
        CurrentRole: string,
        JudgeID: number,
        Nominees: JudgeNominationInfoEntry[]
    ) {
        return this.http.post<JudgeNominationInfo>(
            this.configService.hostAddress +
                `Api/Services/App/FinalizeJudgeNominationTieBreakerGroup`,
            {
                AuthToken: AuthToken || '',
                CurrentRole: CurrentRole || '',
                JudgeID: JudgeID || '',
                Nominees: Nominees || '',
            }
        );
    }

    // #endregion

    // #region Finalize Winner Service Call

    FinalizeWinner(
        AuthToken: string,
        CurrentRole: string,
        AwardID: number,
        SchoolYear: string,
        JudgingPhase: number,
        NominationID: number,
        NomineeTypeID: number,
        BoardDistrict: string,
        FinalScore: number,
		TieBreaker: boolean
    ) {
        return this.http.get<JudgeNominationScoresInfo[]>(
            this.configService.hostAddress +
            `Api/Services/App/FinalizeWinner?` +
            `AuthToken=${AuthToken || ''}&` +
            `CurrentRole=${CurrentRole || ''}&` +
            `AwardID=${AwardID || ''}&` +
            `SchoolYear=${SchoolYear || ''}&` +
            `JudgingPhase=${JudgingPhase || ''}&` +
            `NominationID=${NominationID || ''}&` +
            `NomineeTypeID=${NomineeTypeID || ''}&` +
            `BoardDistrict=${BoardDistrict || ''}&` +
            `FinalScore=${FinalScore || ''}&` +
            `TieBreaker=${TieBreaker || ''}`
        );
    }

    // #endregion

    // #region Get All School Groups Service Call

    GetAllSchoolGroups(AwardID: number, SchoolYear?: string) {
        const self = this;

		return self.http.get<SchoolGroupInfo[]>(
			self.configService.hostAddress + `Api/Services/App/GetAllSchoolGroups?AwardID=${AwardID || ''}&SchoolYear=${SchoolYear || ''}`
		);
    }

    // #endregion

    // #region Get All Schools Service Call

    GetAllSchools(SchoolYear: string) {
        const self = this;

        return self.http.get<SchoolInfo[]>(
			self.configService.hostAddress +
			`Api/Services/App/GetAllSchools` +
			`?SchoolYear=${SchoolYear}`
        )
        .pipe(
            map(schools => {
                self._AllSchools = schools;

                return schools;
            })
        );
    }

    // #endregion

    // #region Get All School Years Service Call

	GetAllSchoolYears(AwardID: number, SchoolYear: string) {
        const self = this;

        return self.http.get<string[]>(self.configService.hostAddress + `Api/Services/App/GetAllSchoolYears?AwardID=${AwardID}&SchoolYear=${SchoolYear}`).pipe(
            map(years => {
                self._AllSchoolYears = years;

                return years;
            })
        );
    }

    // #endregion

    // #region Get Available Awards Rights

    GetAvailableAwardsRights(AwardID?: number) {
        return this.http.get<RoleRightsInfo[]>(
            this.configService.hostAddress +
                `Api/Services/App/GetAvailableAwardsRights?AwardID=${AwardID ||
                    ''}`
        );
    }

    // #endregion

    // #region Get Available Schools

    GetAvailableSchools(AwardID: number, CurrentSchoolYear: string) {
        const self = this;

        if (
            self._AvailableSchools &&
            !!self._AvailableSchools.length &&
            self._LoadedAwardID === AwardID &&
            self._LoadedSchoolYear === CurrentSchoolYear
        ) {
            return of<SchoolInfo[]>(self._AvailableSchools).pipe(take(1));
        }

		// Load the schools, and the school groups (if either is needed), and then parse them together
		const _allSchoolsObs = self.GetAllSchools(CurrentSchoolYear);
        const _allSchoolGroupsObs = self.GetAllSchoolGroups(
            AwardID,
            CurrentSchoolYear
        );

        return combineLatest(_allSchoolsObs, _allSchoolGroupsObs).pipe(
            take(1),
            map(packet => {
                // Update what we have loaded
                self._AllSchools = packet[0];
                self._AllSchoolGroups = packet[1];
                self._LoadedAwardID = AwardID;
                self._LoadedSchoolYear = CurrentSchoolYear;

                // If we have schools loaded, we can now process the list of all schools by incorporating
                // the school groups into it. Parse the schools by iterating all schools. In instances where
                // the school number is part of a known school group, skip adding that school. Once we're done,
                // add in the school groups, and then sort by the name of the school/group lead.
                if (self._AllSchools && !!self._AllSchools.length) {
                    const schools = <SchoolInfo[]>[];

                    self._AllSchools.forEach(sch => {
                        const grp = self._AllSchoolGroups.find(item => {
                            if (item.LeadLocationNumber === sch.SchoolNumber) {
                                return true;
                            }
                            if (item.Schools) {
                                const sgrp = item.Schools.find(
                                    sitem =>
                                        sitem.LocationNumber ===
                                        sch.SchoolNumber
                                );
                                if (sgrp) {
                                    return true;
                                }
                            }
                            return false;
                        });

                        if (!grp) {
                            schools.push(sch);
                        }
                    });

                    // Now we can also add in the school groups by their leader
                    self._AllSchoolGroups.forEach(sch => {
                        schools.push(<SchoolInfo>{
                            SchoolName: sch.Name,
                            SchoolNumber: sch.LeadLocationNumber,
                        });
                    });

                    // Lastly, sort and store
                    self._AvailableSchools = schools.sort((a, b) =>
                        a.SchoolName.localeCompare(b.SchoolName)
                    );

                    return self._AvailableSchools;
                }
            })
        );
    }

    // #endregion

    // #region Get Award Programs Service Call

    GetAwardPrograms() {
        return this._AvailableAwardPrograms
            ? of<AwardProgramInfo[]>(this._AvailableAwardPrograms).pipe(take(1))
            : this.http
                  .get<AwardProgramInfo[]>(
                      this.configService.hostAddress +
                          `Api/Services/App/GetAwardsPrograms`
                  )
                  .pipe(
                      map(atypes => {
                          this._AvailableAwardPrograms = atypes;
                          return atypes;
                      })
                  );
    }

    // #endregion

    // #region Get Award Status Service Call

    GetAwardStatus(AuthToken: string,
        CurrentRole: string,
		AwardID: number,
		SchoolYear: string) {

        return this.http.get<AwardStatusInfo>(
            this.configService.hostAddress +
            `Api/Services/App/GetAwardStatus?` +
            `AuthToken=${AuthToken || ''}&` +
            `CurrentRole=${CurrentRole || ''}&` +
			`AwardID=${AwardID || ''}` +
			`&SchoolYear=${SchoolYear}`
        );
    }

    // #endregion

    // #region Get Current Judge Scores By Phase Service Call

    GetCurrentJudgeScoresByPhase(
        AuthToken: string,
        CurrentRole: string,
        AwardID: number,
        JudgingPhase: number,
        SchoolYear?: string
    ) {
        return this.http.get<JudgeNominationScoresInfo[]>(
            this.configService.hostAddress +
            `Api/Services/App/GetCurrentJudgeScoresByPhase?` +
            `AuthToken=${AuthToken || ''}&` +
            `CurrentRole=${CurrentRole || ''}&` +
            `AwardID=${AwardID || ''}&` +
            `JudgingPhase=${JudgingPhase || ''}&` +
            `SchoolYear=${ SchoolYear || ''}`
        );
    }

    // #endregion

    // #region Get Designee Edit Rights Service Call

    GetDesigneeEditRights(
        AuthToken: string,
        CurrentRole: string,
        LocationNumber: string
    ) {
        return this.http.get<DesigneeEditRights>(
            this.configService.hostAddress +
                `Api/Services/App/GetDesigneeEditRights?` +
                `AuthToken=${AuthToken || ''}&CurrentRole=${CurrentRole ||
                    ''}&LocationNumber=${LocationNumber || ''}`
        );
    }

    // #endregion

    // #region Get Designees Service Call

    GetDesignees(
        AuthToken: string,
        CurrentRole: string,
        AwardID: number,
		LocationNumber: string,
		SchoolYear: string
    ) {
        return this.http.get<DesigneeInfo[]>(
                this.configService.hostAddress +
                `Api/Services/App/GetDesignees?` +
				`AuthToken=${AuthToken || ''}` +
				`&CurrentRole=${CurrentRole || ''}` +
				`&AwardID=${AwardID || ''}` +
				`&LocationNumber=${LocationNumber || ''}` +
				`&SchoolYear=${SchoolYear}`
            )
            .pipe(
                map(data => {
                    if (data && !!data.length) {
                        data.forEach(item => {
                            item.ExpirationDate = item.ExpirationDate
                                ? new Date(item.ExpirationDate)
                                : null;
                        });
                    }

                    return data;
                })
            );
    }

    // #endregion

    // #region Get Form Templates Service Call

    GetFormTemplates() {
        return this._AvailableFormTemplates ? of<FormTemplateInfo[]>(this._AvailableFormTemplates).pipe(take(1)) : this.http
            .get<FormTemplateInfo[]>(this.configService.hostAddress + `Api/Services/App/GetFormTemplates`)
            .pipe(
                map(ntypes => {
                this._AvailableFormTemplates = ntypes;
                    return ntypes;
                })
            );
    }

    // #endregion

    // #region Get Form Window For Type Service Call

    GetFormWindowForType(typeID: number) {
        const self = this;

        if (!self._NominationWindows || !self._NominationWindows.length) {
            return null;
        }

        const formwin = self._NominationWindows.find(item => {
            return (
                item.NomineeTypeID === typeID &&
                item.WindowTypeID === 2 &&
                item.SchoolYear === self._CurrentSchoolYear
            );
        });

        return formwin && formwin.FormTemplateID;
    }

    // #endregion

    // #region Get Judge Judging Rights Service Call

    GetJudgeRights(
        AuthToken: string,
        CurrentRole: string,
        SchoolYear: string
    ) {
        return this.http.get<JudgeJudgingRights>(
            this.configService.hostAddress +
                `Api/Services/App/GetJudgeRights?` +
                `AuthToken=${AuthToken || ''}&CurrentRole=${CurrentRole ||
                    ''}&SchoolYear=${SchoolYear || ''}`
        );
    }

    // #endregion

	// #region Get Judge Nominees Service Call

	GetJudgeNominees(
		AuthToken: string,
		CurrentRole: string,
		AwardID: number,
		JudgeID: number,
		SchoolYear: string
	) {
		return this.http.get<JudgeNominationInfo>(
			this.configService.hostAddress +
			`Api/Services/App/GetJudgeNominees?` +
			`AuthToken=${AuthToken || ''}&` +
			`CurrentRole=${CurrentRole || ''}&` +
			`AwardID=${AwardID || ''}&` +
			`ImpJudgeID=${JudgeID || ''}` +
			`&SchoolYear=${SchoolYear}`
		);
	}

    // #endregion

    // #region Get Judges Service Call

    GetJudges(
        AuthToken: string,
        CurrentRole: string,
        AwardID: number,
        SchoolYear: string
    ) {
        return this.http.get<JudgeInfo[]>(
            this.configService.hostAddress +
                `Api/Services/App/GetJudges?` +
                `AuthToken=${AuthToken || ''}&CurrentRole=${CurrentRole ||
                    ''}&AwardID=${AwardID || ''}&` +
                `SchoolYear=${SchoolYear || ''}`
        );
    }

    // #endregion

    // #region Get Judging Form Service Call

    GetJudgingForm(
        AuthToken: string,
        CurrentRole: string,
        NominationFormUID: string,
        JudgingPhase: number,
		ImpJudgeID: number,
		SchoolYear: string
	){
        return this.http.get<JudgingForm>(
            this.configService.hostAddress +
            `Api/Services/App/GetJudgingForm?` +
            `AuthToken=${AuthToken || ''}&` +
            `CurrentRole=${CurrentRole || ''}&` +
            `NominationFormUID=${NominationFormUID || ''}&` +
            `JudgingPhase=${JudgingPhase || ''}&` +
			`ImpJudgeID=${ImpJudgeID || ''}` +
			`&SchoolYear=${SchoolYear}`
        ).pipe(
            map(form => {
                if (form) {
                    form.DateUpdated = form.DateUpdated ? new Date(form.DateUpdated) : null;
                    form.JudgingDateUpdated = form.JudgingDateUpdated ? new Date(form.JudgingDateUpdated) : null;
                }

                return form;
            })
        );
    }

    // #endregion

    // #region Get Judging Phase Window Type Constant Service Call

    GetJudgingPhaseWindowType(judgingPhase: number) {
        const self = this;

        if (!self._WindowTypes == null || !self._WindowTypes.length) {
            return null;
        }

        for (let wt of self._WindowTypes) {
            const desc = wt.Description.toLowerCase();

            if (wt.JudgingPhase === judgingPhase) {
                return wt.WindowTypeID;
            }
        }

        return null;
    }

    // #endregion

    // #region Get Judging Windows Info Service Call

	GetJudgingWindowsInfo(SchoolYear: string) {
        const self = this;

        // Do we have the windows loaded? We should. If not, we have to load them before continuing.
		self.GetNominationWindows(SchoolYear)
            .pipe(take(1))
            .subscribe(windows => {
                const result = <JudgingWindowsInfo>{};

                // Parse through them looking for initial and final windows
                const today = new Date();
                windows.forEach(win => {
                    const IsOpen = isWithinInterval(
                        today,
                        {
                            start: win.DateBegin,
                            end: win.DateEnd
                        }
                        
                    );
                    const IsBefore = isBefore(today, win.DateBegin);
                    const wtConst = self.GetWindowTypeConstant(
                        win.WindowTypeID
                    );

                    switch (wtConst) {
                        // Initial judging window - This controls if judges for the initial round (to determine the 7 district TOYs)
                        // is open.
                        case WINDOWTYPE.INITIAL_JUDGING_WINDOW:
                            if (win.NomineeTypeID === 1) {
                                result.TOYInitialWindowStatus = IsBefore
                                    ? -1
                                    : IsOpen
                                    ? 0
                                    : 1;
                            } else {
                                result.SREInitialWindowStatus = IsBefore
                                    ? -1
                                    : IsOpen
                                    ? 0
                                    : 1;
                            }
                            break;
                        // Initial tie-breaker judging window - This controls if judges for breaking ties from the initial round is open.
                        case WINDOWTYPE.INITIAL_TIEBREAKER_JUDGING_WINDOW:
                            if (win.NomineeTypeID === 1) {
                                result.TOYInitialTieBreakerWindowStatus = IsBefore
                                    ? -1
                                    : IsOpen
                                    ? 0
                                    : 1;
                            } else {
                                result.SREInitialTieBreakerWindowStatus = IsBefore
                                    ? -1
                                    : IsOpen
                                    ? 0
                                    : 1;
                            }
                            break;
                        // Final judging window - This controls if judges for the final round (to determine the district TOY) is open.
                        case WINDOWTYPE.FINAL_JUDGING_WINDOW:
                            if (win.NomineeTypeID === 1) {
                                result.TOYFinalWindowStatus = IsBefore
                                    ? -1
                                    : IsOpen
                                    ? 0
                                    : 1;
                            } else {
                                result.SREFinalWindowStatus = IsBefore
                                    ? -1
                                    : IsOpen
                                    ? 0
                                    : 1;
                            }
                            break;
                        // Final tie-breaker judging window - This controls if judges for breaking ties from the final round is open.
                        case WINDOWTYPE.FINAL_TIEBREAKER_JUDGING_WINDOW:
                            if (win.NomineeTypeID === 1) {
                                result.TOYFinalTieBreakerWindowStatus = IsBefore
                                    ? -1
                                    : IsOpen
                                    ? 0
                                    : 1;
                            } else {
                                result.SREFinalTieBreakerWindowStatus = IsBefore
                                    ? -1
                                    : IsOpen
                                    ? 0
                                    : 1;
                            }
                            break;
                    }
                });

                return result;
            });
    }

    // #endregion

    // #region Get Nomination By SAPID Service Call

    GetNominationBySAPID(
        AuthToken: string,
        CurrentRole: string,
        AwardID: number,
        NominationTypeID: number,
        SAPID: string,
        CurrentSchoolYear: string
    ) {
        return this.http.get<NomineeInfo>(
            this.configService.hostAddress +
            `Api/Services/App/GetNominationBySAPID?AuthToken=${AuthToken || ''}` +
			`&CurrentRole=${CurrentRole || ''}` +
			`&AwardID=${AwardID || ''}` +
			`&NominationTypeID=${NominationTypeID || ''}` +
			`&SAPID=${SAPID || ''}` +
			`&CurrentSchoolYear=${CurrentSchoolYear || ''}`
        );
    }

    // #endregion

    // #region Get Nomination Form Service Call

    GetNominationForm(
        AuthToken: string,
        CurrentRole: string,
        NominationFormUID: string
    ) {
        return this.http
            .get<NominationForm>(
                this.configService.hostAddress +
                    `Api/Services/App/GetNominationForm?` +
                    `AuthToken=${AuthToken || ''}&CurrentRole=${CurrentRole ||
                        ''}&NominationFormUID=${NominationFormUID || ''}`
            )
            .pipe(
                map(form => {
                    if (form) {
                        form.DateUpdated = form.DateUpdated
                            ? new Date(form.DateUpdated)
                            : null;
                    }

                    return form;
                })
            );
    }

    // #endregion

    // #region Get Nomination Form Public Service Call

    GetNominationFormPublic(NomineeTypeID: number) {
        var res = this.http.get<NominationForm>(
            this.configService.hostAddress +
            `Api/Services/App/GetNominationFormPublic?NomineeTypeID=${NomineeTypeID}`
        );

        return res;
    }

    // #endregion

    // #region Get Nomination Form Instructions Service Call

    GetNominationFormInstructions(
        AuthToken: string,
        NominationFormUID: string
    ) {
        return this.http.get<string>(
            this.configService.hostAddress +
                `Api/Services/App/GetNominationFormInstructions?AuthToken=${AuthToken ||
                    ''}&` +
                `NominationFormUID=${NominationFormUID || ''}`
        );
    }

    // #endregion

    // #region Get Nominations By Work Location Service Call

    GetNominationsByWorkLocation(
        AuthToken: string,
        WorkLocation: string,
        AwardID: number,
        SchoolYear: string
    ) {
        return this.http.get<SchoolNominationInfo>(
            this.configService.hostAddress + `Api/Services/App/GetNominationsByWorkLocation?` +
            `AuthToken=${AuthToken || ''}&` +
            `WorkLocation=${WorkLocation || ''}&` +
            `AwardID=${AwardID || ''}&` +
            `SchoolYear=${SchoolYear || ''}`
        )
        .pipe(map(data => {
            if (data && data.Nominees && !!data.Nominees.length) {
                data.Nominees.forEach(nom => {
                    nom.SiteName = nom.SiteName.replace(/\s\/\s/g, '_');

                    if (nom.Videos && !!nom.Videos.length) {
                        nom.Videos.forEach(vid => {
                            vid.DateUploaded = vid.DateUploaded ? new Date(vid.DateUploaded) : null;
                        });
                    }

                    nom.VideoWindowDeadline = nom.VideoWindowDeadline ? new Date(nom.VideoWindowDeadline) : null;

                    if (nom.Files && !!nom.Files.length) {
                        nom.Files.forEach(fileinfo => {
                            if (fileinfo.Files && !!fileinfo.Files.length) {
                                fileinfo.Files.forEach(file => {
                                    file.DateUploaded = file.DateUploaded ? new Date(file.DateUploaded) : null;
                                });
                            }
                        });
                    }

                    if (nom.FileWindowDeadlines && !!nom.FileWindowDeadlines.length) {
                        nom.FileWindowDeadlines.forEach(win => {
                            win.DateBegin = win.DateBegin ? new Date(win.DateBegin) : null;
                            win.DateEnd = win.DateEnd ? new Date(win.DateEnd) : null;
                        });
                    }
                });
            }

            this._NominationsBySchool = data;
            this.nomByLocationSubject.next(data);
            return data;
        }));
    }

    // #endregion

    // #region Get Nomination Windows Service Call

	GetNominationWindows(SchoolYear: string) {
        const self = this;

        return self.http.get<NominationWindowInfo[]>(self.configService.hostAddress + `Api/Services/App/GetNominationWindows?SchoolYear=${SchoolYear}`).pipe(
            map(info => {
                if (info && !!info.length) {
                    info.forEach(item => {
                        item.DateBegin = item.DateBegin ? new Date(item.DateBegin) : null;
                        item.DateEnd = item.DateEnd ? new Date(item.DateEnd) : null;
                    });
                }

                self._NominationWindows = info;
                return info;
            })
        );
    }

    // #endregion

    // #region Get Nominee Types Service Call

    GetNomineeTypes() {
        return this._AvailableNomineeTypes ? of<NomineeTypeInfo[]>(this._AvailableNomineeTypes).pipe(take(1)) : this.http
            .get<NomineeTypeInfo[]>(this.configService.hostAddress + `Api/Services/App/GetNomineeTypes`)
            .pipe(
                map(ntypes => {
                    this._AvailableNomineeTypes = ntypes;
                    return ntypes;
                })
            );
    }

    // #endregion

    // #region Get RSVP by NominationID Service Call

    GetRsvpByNominationID(AuthToken: string, CurrentRole: string, AwardID: number, SchoolNumber: string, NominationID: number, SchoolYear: string): Observable<RsvpNominee> {
        return this.http.get<RsvpNominee>(this.configService.hostAddress + `Api/Services/App/GetRsvpByNominationID?` +
            `AuthToken=${AuthToken || ''}` +
            `&CurrentRole=${CurrentRole || ''}` +
            `&AwardID=${AwardID || ''}` +
            `&SchoolNumber=${SchoolNumber || ''}` +
			`&NominationID=${NominationID || ''}` +
			`&SchoolYear=${SchoolYear}`
        );
    }

    // #endregion

    // #region Get School Eligibility Service Call

	GetSchoolEligibility(AwardID: number, SchoolYear: string) {

        return this.http.get<Select[]>(
            this.configService.hostAddress +
            `Api/Services/App/GetSchoolEligibility?` +
			`AwardID=${AwardID || ''}` +
			`&SchoolYear=${SchoolYear}`
        );
    }

    // #endregion

    // #region Get School Nominee Type ID Service Call

    GetSchoolNomineeTypeID() {
        if (
            !this._AvailableNomineeTypes ||
            !this._AvailableNomineeTypes.length
        ) {
            return null;
        }

        const nomtype = this._AvailableNomineeTypes.find(
            item => item.Name === 'SchoolRelated'
        );

        return nomtype && nomtype.NomineeTypeID;
    }

    // #endregion

    // #region Get Teacher Nominee Type ID Service Call

    GetTeacherNomineeTypeID() {
        if (
            !this._AvailableNomineeTypes ||
            !this._AvailableNomineeTypes.length
        ) {
            return null;
        }

        const nomtype = this._AvailableNomineeTypes.find(
            item => item.Name === 'Teacher'
        );

        return nomtype && nomtype.NomineeTypeID;
    }

    // #endregion

    // #region Get Window Method Service Call

    GetWindow(windowTypeID: number, NomineeTypeID: number) {
        const self = this;

        if (!self._NominationWindows || !self._NominationWindows.length) {
            return null;
        }

        for (let win of self._NominationWindows) {
            if (win.WindowTypeID === windowTypeID && win.NomineeTypeID === NomineeTypeID) {
                return win;
            }
        }

        return null;
    }

    // #endregion

    // #region Get Window Deadline Service Call

    GetWindowDeadline(windowType: number, nomineeType: number) {
        if (
            windowType == null ||
            nomineeType == null ||
            !this._NominationWindows ||
            !this._NominationWindows.length
        ) {
            return null;
        }

        const win = this._NominationWindows.find(item => {
            const winType = this.GetWindowTypeConstant(item.WindowTypeID);

            return windowType === winType && item.NomineeTypeID === nomineeType;
        });

        return win && win.DateEnd;
    }

    // #endregion

    // #region Get Window Type Constant Service Call

    GetWindowTypeConstant(typeID: number) {
        const self = this;

        if (!self._WindowTypes || !self._WindowTypes.length) {
            return null;
        }

        const wtype = self._WindowTypes.find(
            item => item.WindowTypeID === typeID
        );

        if (!wtype) {
            return null;
        }

        const desc = wtype.Description.toLowerCase();

        if (desc.indexOf('nomination form') >= 0) {
            return WINDOWTYPE.NOMINATIONFORM_WINDOW;
        }
        if (desc.indexOf('photo') >= 0) {
            return WINDOWTYPE.PHOTO_WINDOW;
        }
        if (desc.indexOf('video') >= 0) {
            return WINDOWTYPE.VIDEO_WINDOW;
        }
        if (wtype.JudgingPhase === 1) {
            return WINDOWTYPE.INITIAL_JUDGING_WINDOW;
        }
        if (wtype.JudgingPhase === 2) {
            return WINDOWTYPE.INITIAL_TIEBREAKER_JUDGING_WINDOW;
        }
        if (wtype.JudgingPhase === 3) {
            return WINDOWTYPE.FINAL_JUDGING_WINDOW;
        }
        if (wtype.JudgingPhase === 4) {
            return WINDOWTYPE.FINAL_TIEBREAKER_JUDGING_WINDOW;
        }

        return null;
    }

    // #endregion

    // #region Get Window Types Service Call

    GetWindowTypes() {
        return this._WindowTypes
            ? of<WindowTypeInfo[]>(this._WindowTypes).pipe(take(1))
            : this.http
                  .get<WindowTypeInfo[]>(
                      this.configService.hostAddress +
                          `Api/Services/App/GetWindowTypes`
                  )
                  .pipe(
                      map(wtypes => {
                          this._WindowTypes = wtypes;
                          return wtypes;
                      })
                  );
    }

    // #endregion

    // #region Handle Special Tie Breaker Service Call

    HandleSpecialTieBreaker(
        AuthToken: string,
        CurrentRole: string,
        AwardID: number,
        SchoolYear: string,
        NomineeTypeID: number,
        JudgingPhase: number,
        Ties: JudgeScoresInfo[]
    ) {
        return this.http.post<ScheduleTieBreakerResult>(
            this.configService.hostAddress +
                `Api/Services/App/HandleSpecialTieBreaker`,
            {
                AuthToken: AuthToken || '',
                CurrentRole: CurrentRole || '',
                AwardID: AwardID || '',
                SchoolYear: SchoolYear || '',
                NomineeTypeID: NomineeTypeID || '',
                JudgingPhase: JudgingPhase || '',
                Ties: Ties || '',
            }
        );
    }

    // #endregion

    // #region Is App Online Service Call

    IsAppOnline() {
        return this.http.get<boolean>(
            this.configService.hostAddress +
            `Api/Services/App/IsAppOnline`
        );
    }

    // #endregion

    // #region Is Judging Window Open Service Call

    IsJudgingWindowOpen(
        AuthToken: string,
        CurrentRole: string,
        CheckTeachers: boolean,
        CheckSchools: boolean,
        SchoolYear: string
    ) {
        return this.http.get<boolean>(
            this.configService.hostAddress +
            `Api/Services/App/IsJudgingWindowOpen?` +
			`AuthToken=${AuthToken || ''}` +
			`&CurrentRole=${CurrentRole || ''}` +
			`&CheckTeachers=${CheckTeachers || ''}` +
			`&CheckSchools=${CheckSchools || ''}` +
			`&SchoolYear=${SchoolYear || ''}`
        );
    }

    // #endregion

    // #region Remove Designee Service Call

    RemoveDesignee(
        AuthToken: string,
        CurrentRole: string,
        AwardID: number,
        DesigneeID: number,
		CurrentSchool: string,
		SchoolYear: string
    ) {
        return this.http.get<DesigneeInfo[]>(
            this.configService.hostAddress +
            `Api/Services/App/RemoveDesignee?` +
			`AuthToken=${AuthToken || ''}` +
			`&AwardID=${AwardID || ''}` +
			`&DesigneeID=${DesigneeID || ''}` +
			`&CurrentRole=${CurrentRole || ''}` +
			`&CurrentSchool=${CurrentSchool || ''}` +
			`&SchoolYear=${SchoolYear}`
        );
    }

    // #endregion

	// #region Reset Judge Groups Service Call

	ResetJudgeGroups(
		AuthToken: string,
		CurrentRole: string,
		SchoolYear: string
	) {
		return this.http.post<any>(this.configService.hostAddress + 'Api/Services/App/ResetJudgeGroups',
			{
				AuthToken: AuthToken || '',
				CurrentRole: CurrentRole || '',
				SchoolYear: SchoolYear
			}
		);
	}

    // #endregion

    // #region Remove Judge Service Call

    RemoveJudge(AuthToken: string, CurrentRole: string, JudgeID: number) {
        return this.http.get<JudgeInfo[]>(
            this.configService.hostAddress +
                `Api/Services/App/RemoveJudge?` +
                `AuthToken=${AuthToken || ''}&CurrentRole=${CurrentRole ||
                    ''}&JudgeID=${JudgeID || ''}`
        );
    }

    // #endregion

    // #region Remove Nominee Service Call

    RemoveNominee(
        AuthToken: string,
        CurrentRole: string,
        NominationID: number,
        AwardID: number,
		WorkLocation: string,
		SchoolYear: string
    ) {
        return this.http.post<NomineeInfo>(
            this.configService.hostAddress + `Api/Services/App/RemoveNominee`,
            {
                AuthToken: AuthToken || '',
                CurrentRole: CurrentRole || '',
                NominationID: NominationID || '',
                AwardID: AwardID,
				WorkLocation: WorkLocation,
				SchoolYear: SchoolYear
            }
        );
    }

    // #endregion

    // #region Remove School Group Service Call

    RemoveSchoolGroup(
        AuthToken: string,
        CurrentRole: string,
        SchoolGroupID: number
    ) {
        return this.http.get<SchoolGroupInfo[]>(
            this.configService.hostAddress +
                `Api/Services/App/RemoveSchoolGroup?` +
                `AuthToken=${AuthToken || ''}&CurrentRole=${CurrentRole ||
                    ''}&SchoolGroupID=${SchoolGroupID || ''}`
        );
    }

    // #endregion

    // #region Save Judging Form Service Call

    SaveJudgingForm(
        AuthToken: string,
        CurrentRole: string,
		JudgeForm: JudgeSaveForm,
		SchoolYear: string
    ) {
        return this.http
            .post<JudgingForm>(
                this.configService.hostAddress +
                    `Api/Services/App/SaveJudgingForm`,
                {
                    AuthToken: AuthToken || '',
                    CurrentRole: CurrentRole || '',
					JudgingForm: JudgeForm || '',
					SchoolYear: SchoolYear
                }
            )
            .pipe(
                map(form => {
                    if (form) {
                        form.DateUpdated = form.DateUpdated
                            ? new Date(form.DateUpdated)
                            : null;
                        form.JudgingDateUpdated = form.JudgingDateUpdated
                            ? new Date(form.JudgingDateUpdated)
                            : null;
                    }

                    return form;
                })
            );
    }

    // #endregion

    // #region Save Nomination Form Service Call

    SaveNominationForm(
        AuthToken: string,
        CurrentRole: string,
        NomForm: NominationSaveForm
    ) {
        return this.http
            .post<NominationForm>(
                this.configService.hostAddress +
                    `Api/Services/App/SaveNominationForm`,
                {
                    AuthToken: AuthToken || '',
                    CurrentRole: CurrentRole || '',
                    NominationForm: NomForm || '',
                }
            )
            .pipe(
                map(form => {
                    if (form) {
                        form.DateUpdated = form.DateUpdated
                            ? new Date(form.DateUpdated)
                            : null;
                    }

                    return form;
                })
            );
    }

    // #endregion

    // #region Save Nomination Form Public Service Call

    SaveNominationFormPublic(NomForm: NominationSaveFormPublic) {
        let upload: FormData = new FormData();
        upload.append('photo', NomForm.Photo);
        upload.append('responses', JSON.stringify(NomForm.Form));
		upload.append('captcha', NomForm.Captcha);
		upload.append('schoolYear', NomForm.SchoolYear);
        
        return this.http.post<any>(this.configService.hostAddress + `Api/Services/App/SaveNominationFormPublic`, upload);
    }

    // #endregion

    // #region Save RSVP Form Service Call

    SaveRsvpForm(form: RsvpSaveParameter) {
        return this.http.post<RsvpSaveParameter>(this.configService.hostAddress + `Api/Services/App/SaveRsvp`, form);
    }

    // #endregion

    // #region Schedule Tie Breaker Service Call

    ScheduleTieBreaker(
        AuthToken: string,
        CurrentRole: string,
        AwardID: number,
        SchoolYear: string,
        NomineeTypeID: number,
        JudgingPhase: number,
        Ties: JudgeScoresInfo[],
        CopyJudges: boolean,
        NeedsWindow: boolean,
        DateBegin: Date,
        DateEnd: Date
    ) {
        return this.http
            .post<ScheduleTieBreakerResult>(
                this.configService.hostAddress +
                    `Api/Services/App/ScheduleTieBreaker`,
                {
                    AuthToken: AuthToken || '',
                    CurrentRole: CurrentRole || '',
                    AwardID: AwardID || '',
                    SchoolYear: SchoolYear || '',
                    NomineeTypeID: NomineeTypeID || '',
                    JudgingPhase: JudgingPhase || '',
                    Ties: Ties || '',
                    CopyJudges: CopyJudges || '',
                    NeedsWindow: NeedsWindow || '',
                    DateBegin: DateBegin ? DateBegin.toJSON() : '',
                    DateEnd: DateEnd ? DateEnd.toJSON() : '',
                }
            )
            .pipe(
                map(data => {
                    if (data.Windows && !!data.Windows.length) {
                        data.Windows.forEach(win => {
                            // Convert everything to dates for later comparison.
                            win.DateBegin = win.DateBegin
                                ? new Date(win.DateBegin)
                                : null;
                            win.DateEnd = win.DateEnd
                                ? new Date(win.DateEnd)
                                : null;
                        });
                    }

                    return data;
                })
            );
    }

    // #endregion

    // #region Search For Employee By SAPID Servic Call

    SearchForEmployeeBySAPID(AuthToken: string, SAPID: string) {
        return this.http.get<EmployeeInfo[]>(
            this.configService.hostAddress +
                `Api/Services/App/SearchForEmployeeBySAPID?AuthToken=${AuthToken ||
                    ''}&SAPID=${SAPID || ''}`
        );
    }

    // #endregion

    // #region Search For Judge User Service Call

    SearchForJudgeUser(
        AuthToken: string,
        CurrentRole: string,
        FirstName?: string,
        LastName?: string,
        SAPID?: string,
        Username?: string
    ) {
        return this.http.post<UserSearchResult[]>(
            this.configService.hostAddress +
                `Api/Services/App/SearchForJudgeUser`,
            {
                AuthToken: AuthToken || '',
                CurrentRole: CurrentRole || '',
                FirstName: FirstName || '',
                LastName: LastName || '',
                SAPID: SAPID || '',
                Username: Username || '',
            }
        );
    }

    // #endregion

    // #region Search For User Service Call

    SearchForUser(
        AuthToken: string,
		CurrentRole: string,
		AwardID: number,
		SchoolYear: string,
		FirstName?: string,
		LastName?: string,
		SAPID?: string,
		ForcedLocationNumber?: string
    ) {
        return this.http.post<UserSearchResult[]>(
            this.configService.hostAddress + `Api/Services/App/SearchForUser`,
            {
                AuthToken: AuthToken,
				CurrentRole: CurrentRole,
				AwardID: AwardID,
				SchoolYear: SchoolYear,
                FirstName: FirstName || '',
                LastName: LastName || '',
				SAPID: SAPID || '',
				SchoolNumber: ForcedLocationNumber
            }
        );
    }

    // #endregion

	// #region Set Eligibility Service Call

	SetEligibility(form: EligibilitySaveParameters) {
		return this.http.post<EligibilitySaveParameters>(this.configService.hostAddress + `Api/Services/App/SetEligibility`, form);
	}

    // #endregion

    // #region Update Designee Service Call

    UpdateDesignee(
        AuthToken: string,
        CurrentRole: string,
        AwardID: number,
        DesigneeID: number,
        UserID: number,
        SAPID: string,
        PositionNo: string,
        LocationNumber: string,
        ExpirationDate: Date,
		Rights: number[],
		SchoolYear: string
    ) {
        return this.http.post<DesigneeInfo[]>(
            this.configService.hostAddress + `Api/Services/App/UpdateDesignee`,
            {
                AuthToken: AuthToken || '',
                AwardID: AwardID || '',
                DesigneeID: DesigneeID || '',
                CurrentRole: CurrentRole || '',
                UserID: UserID || '',
                SAPID: SAPID || '',
                PositionNo: PositionNo || '',
                LocationNumber: LocationNumber || '',
                ExpirationDate: ExpirationDate || '',
				Rights: Rights || '',
				SchoolYear: SchoolYear
            }
        );
    }

    // #endregion

    // #region Upsert Judge Service Call

    UpsertJudge(
        AuthToken: string,
        CurrentRole: string,
        AwardID: number,
        JudgeID: number,
        FirstName: string,
        LastName: string,
        Email: string,
        DistrictNumber: string,
        NomineeTypeID: number,
        JudgingPhase: number,
		SendEmail: boolean,
		SchoolYear: string
    ) {
        return this.http.get<JudgeInfo[]>(
            this.configService.hostAddress + `Api/Services/App/UpsertJudge?` +
            `AuthToken=${AuthToken || ''}&` +
            `CurrentRole=${CurrentRole || ''}&` +
            `JudgeID=${JudgeID || ''}&` +
            `AwardID=${AwardID || ''}&` +
            `FirstName=${FirstName || ''}&` +
            `LastName=${LastName || ''}&` +
            `Email=${Email || ''}&` +
            `DistrictNumber=${DistrictNumber || ''}&` +
            `NomineeTypeID=${NomineeTypeID || ''}&` +
            `JudgingPhase=${JudgingPhase || ''}&` +
			`SendEmail=${SendEmail || ''}` +
			`&SchoolYear=${SchoolYear}`);
    }

    // #endregion

    // #region Upsert Judge List Service Call

    UpsertJudgeList(
        AuthToken: string,
        CurrentRole: string,
		Judges: JudgeInfo[],
		SchoolYear: string
    ) {
        return this.http.post<JudgeInfo[]>(
            this.configService.hostAddress + `Api/Services/App/UpsertJudgeList`,
            {
                AuthToken: AuthToken,
                CurrentRole: CurrentRole,
				JudgeList: Judges,
				SchoolYear: SchoolYear
            });
    }

    // #endregion

    // #region Update Judges Service Call

    UpdateJudges(judges: JudgeInfo[]) {
        // Store
        this._Judges = judges;
    }

    // #endregion

    // #region Update Nomination Windows Service Call

    UpdateNominationWindows(windows: NominationWindowInfo[]) {
        const self = this;

        // Store
        this._NominationWindows = windows || [];

        // Now we can also update the various flags associated with these
        const today = new Date();

        this._NominationWindows.forEach(win => {
            const IsOpen = isWithinInterval(today, { start: win.DateBegin, end: win.DateEnd });
            const wtConst = self.GetWindowTypeConstant(win.WindowTypeID);

            switch (wtConst) {
                // Nomination form window - This controls whether or not nominees can be chosen, and also if
                // the form can be submitted with changes
                case WINDOWTYPE.NOMINATIONFORM_WINDOW:
                    if (win.NomineeTypeID === 1) {
                        self._TOYFormWindowOpen = IsOpen;
                    } else {
                        self._SREFormWindowOpen = IsOpen;
                    }
                    break;
                // Photo form window - This controls whether or not the photo can be uploaded, removed, etc.
                case WINDOWTYPE.PHOTO_WINDOW:
                    if (win.NomineeTypeID === 1) {
                        self._TOYPhotoWindowOpen = IsOpen;
                    } else {
                        self._SREPhotoWindowOpen = IsOpen;
                    }
                    break;
                // Video form window - This controls whether or not the video can be uploaded, removed, etc.
                case WINDOWTYPE.VIDEO_WINDOW:
                    if (win.NomineeTypeID === 1) {
                        self._TOYVideoWindowOpen = IsOpen;
                    } else {
                        self._SREVideoWindowOpen = IsOpen;
                    }
                    break;
                // Initial judging window - This controls if judges for the initial round (to determine the 7 district TOYs) is open.
                case WINDOWTYPE.INITIAL_JUDGING_WINDOW:
                    if (win.NomineeTypeID === 1) {
                        self._TOYJudgeInitialWindowOpen = IsOpen;
                    } else {
                        self._SREJudgeInitialWindowOpen = IsOpen;
                    }
                    break;
                // Initial tie-breaker judging window
                case WINDOWTYPE.INITIAL_TIEBREAKER_JUDGING_WINDOW:
                    if (win.NomineeTypeID === 1) {
                        self._TOYJudgeInitialTieBreakerWindowOpen = IsOpen;
                    } else {
                        self._SREJudgeInitialTieBreakerWindowOpen = IsOpen;
                    }
                    break;
                // Final judging window - This controls if judges for the final round (to determine the district TOY) is open.
                case WINDOWTYPE.FINAL_JUDGING_WINDOW:
                    if (win.NomineeTypeID === 1) {
                        self._TOYJudgeFinalWindowOpen = IsOpen;
                    } else {
                        self._SREJudgeFinalWindowOpen = IsOpen;
                    }
                    break;
                // Final tie-breaker judging window
                case WINDOWTYPE.FINAL_TIEBREAKER_JUDGING_WINDOW:
                    if (win.NomineeTypeID === 1) {
                        self._TOYJudgeFinalTieBreakerWindowOpen = IsOpen;
                    } else {
                        self._SREJudgeFinalTieBreakerWindowOpen = IsOpen;
                    }
                    break;
            }
        });
    }

    // #endregion

    // #region Update School Group Service Call

    UpdateSchoolGroup(
        AuthToken: string,
        CurrentRole: string,
        AwardID: number,
		dbPacket: any
    ) {
        return this.http.post<SchoolGroupInfo[]>(
            this.configService.hostAddress +
                `Api/Services/App/UpdateSchoolGroup`,
            {
                AuthToken: AuthToken || '',
                CurrentRole: CurrentRole || '',
                AwardID: AwardID || '',
				SchoolGroup: dbPacket
            }
        );
    }

    // #endregion

    // #region Update Window Service Call

    UpdateWindow(
        AuthToken: string,
        CurrentRole: string,
        AwardID: number,
        NominationWindowID: number,
        FormTemplateID: number,
        DateBegin: Date,
		DateEnd: Date,
		SchoolYear: string
    ){
        return this.http.get<NominationWindowInfo[]>(
            this.configService.hostAddress + `Api/Services/App/UpdateWindow?AuthToken=${AuthToken}&CurrentRole=${CurrentRole}&AwardID=${AwardID}&NominationWindowID=${NominationWindowID}&FormTemplateID=${FormTemplateID}&DateBegin=${DateBegin?DateBegin.toJSON() : ''}&DateEnd=${DateEnd ? DateEnd.toJSON() : ''}&SchoolYear=${SchoolYear}`
        ).pipe(
            map(windows => {
                // Convert everything to dates for later comparison.
                if (windows && !!windows.length) {
                    windows.forEach(win => {
                        win.DateBegin = win.DateBegin
                            ? new Date(win.DateBegin)
                            : null;
                        win.DateEnd = win.DateEnd
                            ? new Date(win.DateEnd)
                            : null;
                    });
                }

                return windows;
            })
        );
    }

    // #endregion

    // #region Upload Judge Spreadsheet Service Call

    UploadJudgeSpreadsheet(
        AuthToken: string,
        CurrentRole: string,
		Spreadsheet: File,
		SchoolYear: string,
		AwardID: number
    ): Observable<JudgeInfo[]> {
        let packet: FormData = new FormData;
        packet.append('AuthToken', AuthToken);
        packet.append('CurrentRole', CurrentRole);
		packet.append('Spreadsheet', Spreadsheet);
		packet.append('SchoolYear', SchoolYear);
		packet.append('AwardID', AwardID.toString());

        return this.http.post<JudgeInfo[]>(this.configService.hostAddress + `Api/Services/App/UploadJudgeSpreadsheet`, packet);
    }

    // #endregion

    // #region Upsert Nomination Service Call

    UpsertNomination(
        AuthToken: string,
        CurrentRole: string,
        AwardID: number,
        NominationID: number,
        SAPID: string,
        SchoolYear: string,
        NomineeTypeID: number,
        WorkLocation: string,
        StudentID: string,
        PersonType: string,
        FirstName: string,
        LastName: string,
		Email: string
    ) {
        return this.http
            .post<NomineeInfo>(
                this.configService.hostAddress +
                    `Api/Services/App/UpsertNomination`,
                {
                    AuthToken: AuthToken || '',
                    CurrentRole: CurrentRole || '',
                    AwardID: AwardID || '',
                    NominationID: NominationID || '',
                    SAPID: SAPID || '',
                    SchoolYear: SchoolYear || '',
                    NomineeTypeID: NomineeTypeID || '',
                    WorkLocation: WorkLocation || '',
                    StudentID: StudentID || '',
                    PersonType: PersonType || '',
                    FirstName: FirstName || '',
                    LastName: LastName || '',
                    Email: Email || ''
                }
            )
            .pipe(
                map(body => {
                    if (body) {
                        body.DateNominated = body.DateNominated
                            ? new Date(body.DateNominated)
                            : null;
                    }

                    return body;
                })
            );
    }

    // #endregion
}

// #region Custom Classes

// #region Attachment Deadline Info Class

export class AttachmentDeadlineInfo {
    AttachmentTypeID?: number;
    Code?: string;
    DateBegin?: Date;
    DateEnd?: Date;
    WindowOpen?: boolean;
}

// #endregion

// #region Attachment Type Info Class

export class AttachmentTypeInfo {
    AttachmentTypeID?: number;
    AwardID?: number;
    Code?: string;
    Description?: string;
    ExtensionList?: string[];
    Extensions: string;
    Instructions?: string;
    Name?: string;
    NomineeProvides?: boolean;
    SizeLimit?: number;
	LowerSizeLimit?: number;
	Resolution: string;
    DownloadFile?: string;
}

// #endregion

// #region Award Status Info Class

export class AwardStatusInfo {
    DefaultRoleRights: DefaultRoleRightsInfo[];
    SchoolGroups: SchoolGroupInfo[];
    PointScales: JudgePointScaleInfo[];
    EligibilityCount: number;
    NomineeTypeInfoList: NomineeTypeStatusInfo[];
}

export class NomineeTypeStatusInfo {
    NominationName: string;
    FormTemplate: FormTemplateStatus;
    RsvpFormTemplate: RsvpGuest;
    AttachmentTypes: AttachmentTypeInfo[];
    WindowDates: NominationWindowInfo[];
    Phases: JudgeRubricInfo[];
}

// #endregion

// #region Designee Edit Rights Class

export class DesigneeEditRights {
    CanEditTeacher: boolean;
    CanEditSchool: boolean;
    CanUploadVideos: boolean;
    CanNominate: boolean;
}

// #endregion

// #region Default Role Rights Info Class

export class DefaultRoleRightsInfo {
    RoleName: string;
    Active: boolean;
    RightsList: RightsInfo[];
}

export class RightsInfo {
    RightsCode: string;
    RightsName: string;
    CanBeAssigned: boolean;
    RequiresSystemAdmin: boolean;
    RequiresDeveloper: boolean;
}

// #endregion

// #region Designee Info Class

export class DesigneeInfo {
    DesigneeID?: number;
    UserID?: number;
    SAPID?: string;
    PositionNo?: string;
    FirstName?: string;
    LastName?: string;
    Title?: string;
    EmployeeActive?: boolean;
    LocationNumber?: string;
    SiteName?: string;
    CreatedDate?: Date;
    ExpirationDate?: Date;
    DesigneeActive?: boolean;

    Rights?: ActiveRightsInfo[];
}

// #endregion

// #region Eligibility Save Class

export class EligibilitySaveParameters {
    AuthToken: string;
    CurrentRole: string;
    AwardID: number;
	Data: CheckBoxResponse[];
	SchoolYear: string;
}

export class CheckBoxResponse {
    SchoolNumber: string;
    SchoolName: string;
    IsChecked: boolean;
}

// #endregion

// #region Employee Info Class

export class EmployeeInfo {
    SAPID: string;
    StudentID: string;
    LastName: string;
    FirstName: string;
    LocalJobTitle: string;
    UserID?: number;
    WorkLocation: string;
    SiteName?: string;
    PositionNo: string;
}

// #endregion

// #region File Attachment Info Class

export class FileAttachmentInfo {
    AttachmentTypeID?: number;
    FileName?: string;
    FileSize?: number;
    FileStorageURL?: string;
    FileStatus?: string;
    FileTypeCode?: string;
    FileTypeName?: string;
    FileAttachmentID?: number;
    IsInProgress?: boolean;
    NominationFormUID?: string;
    OriginalFileName?: string;
    Uploader?: number;
    UploaderName?: string;
    DateUploaded?: Date;
    UploadID?: number;
    UploadPercentComplete?: number;
}

// #endregion

// #region Form Template Info Class

export class FormTemplateInfo {
    FormTemplateID?: number;
    Name?: string;
    AwardID?: number;
}

// #endregion

// #region Form Template Status Class

export class FormTemplateStatus {
    TemplateName: string;
    Sections: FormTemplateSectionStatus[];
}

export class FormTemplateSectionStatus {
    SectionName: string;
    QuestionList: NominationFormSectionQuestion[];
}

// #endregion

// #region Judge Judging Rights Class

export class JudgeJudgingRights {
    SchoolYear?: string;
    DistrictNumber?: string;
    CanJudgeTeacher?: boolean;
    CanJudgeSchool?: boolean;
}

// #endregion

// #region Judge Nomination Info Class

export class JudgeNominationInfo {
    GroupNumber?: number;
    SchoolYear?: string;
    DistrictNumber?: string;
    NomineeTypeID?: number;
    JudgingPhase?: number;
    JudgeID?: number;
    Finalized?: boolean;
    Nominees?: JudgeNominationInfoEntry[];
    NumberCompleted?: number;
    AvailableRankings?: number[];

    // For UI
    JudgingWindowOpen?: boolean;
}

// #endregion

// #region Judge Nomination Info Entry Class

export class JudgeNominationInfoEntry {
    NominationID?: number;
    SchoolYear?: string;
    NomineeTypeID?: number;
    DistrictNumber?: string;
    NominationFormUID?: string;
    JudgeRatingUID?: string;
    JudgeRatingVersion?: number;
    RatingJudgingPhase?: number;
    JudgeID?: number;
    DateUpdated?: Date;
    Finalized?: boolean;
    RatingsGiven?: number;
    RatingsAvailable?: number;
    Completed?: boolean;
    JudgingPhase?: number;
    SelectedRanking?: number;
    NominationForm?: NominationForm;
}

// #endregion

// #region Judge Nomination Phase Info Class

export class JudgeNominationPhaseInfo {
    JudgingPhase?: number;
    AnyAvailable?: boolean;
    NominationGroups?: JudgeNominationInfo[];
}

// #endregion

// #region Judge Point Scale Info Class

export class JudgePointScaleInfo {
    Name: string;
    Details: JudgePointScaleDetailInfo[];
}

export class JudgePointScaleDetailInfo {
    Text: string;
    Points: number;
}

// #endregion

// #region Judge Rubric Info Class

export class JudgeRubricInfo {
    Phase: number;
    SectionsExpanded: boolean;
    Sections: JudgeRubricSectionInfo[];
}

export class JudgeRubricSectionInfo {
    SectionName: string;
    Questions: JudgeRubricQuestionInfo[];
}

export class JudgeRubricQuestionInfo {
    Question: string;
    PointScale: string;
}

// #endregion

// #region Judge Save Form Class

export class JudgeSaveForm {
    NominationFormUID: string;
    NominationID: number;
    JudgeID?: number;
    JudgeRatingUID?: string;
    JudgeRatingVersion?: number;
    JudgingPhase?: number;
    Finalized?: boolean;
    FormData: JudgeSaveFormData[];
}

// #endregion

// #region Judge Save Form Data Class

export class JudgeSaveFormData {
    RubricDetailID: number;
    Score: number;
}

// #endregion

// #region Judge District Scores Info Class

export class JudgeDistrictScoresInfo {
    BoardDistrict?: string;
    WinnerFinalized?: boolean;
    Scores?: JudgeScoresInfo[];
}

// #endregion

// #region Judge Nomination Scores Info Class

export class JudgeNominationScoresInfo {
    NomineeTypeID?: number;
    JudgingPhase?: number;
    DistrictList: JudgeDistrictScoresInfo[];
}

// #endregion

// #region Judge Info Class

export class JudgeInfo {
    JudgeID?: number;
    AwardID?: number;
    JudgingKey?: string;
    SortableName?: string;
    FirstName?: string;
    LastName?: string;
    Email?: string;
    DistrictNumber?: string;
    DistrictUser?: string;
    NomineeTypeID?: number;
    NomineeTypeName?: string;
    NomineeTypeUser?: string;
    SchoolYear?: string;
    WindowTypeID?: number;
    JudgingPhase?: number;
    PhaseName?: string;
    PhaseUser?: string;
    Finalized?: boolean;
    SendEmail?: boolean;
    Duplicate?: boolean;
    DbDuplicate?: boolean;
    Errors?: boolean[];
}

// #endregion

// #region Judge Scores Individual Info Class

export class JudgeScoresIndividualInfo {
    JudgeID?: number;
    JudgeScore?: number;
    FirstName?: string;
    LastName?: string;
    Email?: string;
}

// #endregion

// #region Judge Scores Info Class

export class JudgeScoresInfo {
    BoardDistrict?: string;
    NominationID?: number;
    SchoolYear?: string;
    SAPID?: string;
    NomineeTypeID?: number;
    FirstName?: string;
    LastName?: string;
    LocalJobTitle?: string;
    SiteLocation?: string;
    SiteName?: string;
    NominationFormUID?: string;
    FinalScore?: number;
    FinalRank?: number;
    WinnerTie?: boolean;
    FinalizedWinner?: boolean;
    JudgingPhase?: number;
    Details?: JudgeScoresIndividualInfo[];

    // Used for UI
    DetailsShown?: boolean;
    SelectedRanking?: number;
}

// #endregion

// #region Nomination Form Class

export class NominationForm {
    NomineeTypeID?: number;
    NominationFormUID?: string;
    NominationFormVersion?: number;
    NominationID?: number;
    FormTemplateID?: number;
    DateUpdated?: Date;
    Deleted?: boolean;
    FormInstructions?: string;
    Sections?: NominationFormSection[];
    Files?: NomineeFileInfo[];
    Photos?: FileAttachmentInfo[];
    Videos?: FileAttachmentInfo[];

    // For the UI
    ShowingNominationForm?: boolean;
    SelectedTabIndex?: number;
}

// #endregion

// #region Judging Form Class

export class JudgingForm extends NominationForm {
    JudgeRatingUID?: string;
    JudgeRatingVersion?: number;
    JudgeID?: number;
    JudgeUserID?: number;
    JudgingDateUpdated?: Date;
    JudgingPhase?: number;
    Finalized?: boolean;
    Details?: JudgingFormDetail[];
}

// #endregion

// #region Judging Form Detail Class

export class JudgingFormDetail {
    JudgeRatingDetailID?: number;
    JudgeRubricDetailID?: number;
    Score?: number;
    UnweightedScore?: number;
    FormTemplateSectionID?: number;
    FormTemplateSortOrder?: number;
    Text?: string;
    RubricPointScaleID?: number;
    SortOrder?: number;
}

// #endregion

// #region Judging Windows Info Class

export class JudgingWindowsInfo {
    TOYInitialWindowStatus?: number;
    TOYInitialTieBreakerWindowStatus?: number;
    TOYFinalWindowStatus?: number;
    TOYFinalTieBreakerWindowStatus?: number;
    SREInitialWindowStatus?: number;
    SREInitialTieBreakerWindowStatus?: number;
    SREFinalWindowStatus?: number;
    SREFinalTieBreakerWindowStatus?: number;
}

// #endregion

// #region New User Info Class

export class NewUserInfo {
    Username?: string;
    UserID?: number;
    Password?: string;
    Email?: string;
    NewAccount?: boolean;
    FirstName?: string;
    LastName?: string;
}

// #endregion

// #region Nomination Form Section Class

export class NominationFormSection {
    SectionID: number;
    Name: string;
    RomanNumeral?: string;
    Instructions: string;
    SortOrder: number;
    SectionReady: boolean;
    IsAttachment: boolean;
    AttachmentType: AttachmentTypeInfo;
    PrincipalOnly: boolean;
    JudgingAvailable?: boolean;
    DownloadFile: boolean;
    Questions: NominationFormSectionQuestion[];
    RubricQuestions: RubricSectionEntry[];
    RubricQuestionsScored: number;
}

// #endregion

// #region Nomination Form Section Question Class

export class NominationFormSectionQuestion {
    SectionID: number;
    SectionDetailID: number;
    QuestionID: number;
    QuestionTypeID: number;
    QuestionText: string;
    Text2: string;
    QuestionNarrativeID: number;
    NarrativeText: string;
    TextLimit: number;
    TextLimitUnit: number;
    LowerTextLimit: number;
    Response: any;
    SortOrder: number;
    ShowNumber: boolean;
    ShowNarrative: boolean;
    QuestionNumber: number;
    DigitOnly: boolean;
    ResponseInvalid: boolean;
    InitiallyBlank: boolean;
    TextSizeCount: number;
    OtherResponse: string;
    HasOptions: boolean;
    CanSelectMultiple: boolean;
    QuestionType: string;
    Options: NominationFormSectionQuestionOption[];
    OtherSelected: boolean;
    FirstName: string;
    LastName: string;
    Inline: boolean;
	Required: boolean;
	DeclineJudging: boolean;
	ExpandedText: string[];
}

// #endregion

// #region Nomination Form Section Question Option Class

export class NominationFormSectionQuestionOption {
    DisplayText: string;
    Value: number;
    RequiresNote: boolean;
}

// #endregion

// #region Nomination Save Form Class

export class NominationSaveForm {
    NominationFormUID: string;
    FormData: NominationSaveFormData[];
}

// #endregion

// #region Nomination Save Form Public Class

export class NominationSaveFormPublic {
    Photo: File;
    Form: NominationSaveFormData[];
	Captcha: string;
	SchoolYear: string;
}

// #endregion

// #region Nomination Save Form Data Class

export class NominationSaveFormData {
    FormTemplateSectionID: number;
    FormTemplateSectionDetailID: number;
    QuestionID: number;
    Response: string;
    OtherResponse: string;
}

// #endregion

// #region Nomination Window Info Class

export class NominationWindowInfo {
    NominationWindowID: number;
    AwardID: number;
    SchoolYear: string;
    WindowTypeID: number;
    WindowTypeDescription: string;
    WindowTypeCode: string;
    IsAttachment: boolean;
    JudgingPhase: number;
    NomineeTypeID: number;
    FormTemplateID: number;
    DateBegin: Date;
    DateEnd: Date;
    Open: boolean;
    Upcoming: boolean;
    Past: boolean;
}

// #endregion

// #region Nominee File Info Class

export class NomineeFileInfo {
    NominationFormUID?: string;
    AttachmentTypeID?: number;
    Code?: string;
    Files?: FileAttachmentInfo[];
    NomineeProvides?: boolean;
}

// #endregion

// #region Nominee Info Class

export class NomineeInfo extends EmployeeInfo {
    NominationID?: number;
    SchoolYear?: string;
    NomineeTypeID?: number;
    DateNominated?: Date;
    NominationFormUID?: string;
    PersonType: string;
    GradeLevel: string;
    Email: string;
    NominationForm?: NominationForm;
    Videos?: FileAttachmentInfo[];
    VideoWindowDeadline?: Date;
    VideoWindowOpen?: boolean;

    Files?: NomineeFileInfo[];
    FileWindowDeadlines?: AttachmentDeadlineInfo[];
}

// #endregion

// #region Nominee Type Info Class

export class NomineeTypeInfo {
    NomineeTypeID: number;
    AwardID: number;
    PersonType: string;
    GradeLevel: string;
    GradeDisplay: string;
    Name: string;
    Description: string;
    EligibilityStatement: string;
    EmailDescription: string;
    ShortName: string;
    FileStorageRoot: string;
    SortOrder?: number;
    AttachmentTypes?: AttachmentTypeInfo[];
}

// #endregion

// #region Judge Pool Class

export class JudgePool {
    PoolSize: number;
    PoolCount: number;
    MinJudges: number;
    Table: JudgePoolTable[];
    Invalid: boolean;
}

export class JudgePoolTable {
    JudgePerGroup: number;
    NomPerGroup: number;
}

// #endregion

// #region Role Rights Info Class

export class RoleRightsInfo {
    AwardID: number;
    RoleRightsID: number;
    RightsCode: string;
    Description: string;
    RightsName: string;
    CanBeAssigned: boolean;
    RequiresSystemAdmin: boolean;
    RequiresDeveloper: boolean;
}

// #endregion

// #region Active Rights Info Class

export class ActiveRightsInfo extends RoleRightsInfo {
    DesigneeRightsID: number;
    RoleActive: boolean;
}

// #endregion

// #region RSVP Info Class

export class RsvpSchool {
    SchoolName: string;
    NomineeList: RsvpNominee[];
}

export class RsvpNominee {
    NominationID: number;
    NominineeFirst: string;
    NomineeLast: string;
    NomineeAttendance: boolean;
    MaxGuestCount: number;
    GuestList: RsvpGuest[];
}

export class RsvpGuest {
    GuestNumber: number;
    QuestionList: NominationFormSectionQuestion[];
}

// #endregion

// #region RSVP Save Parameter Class

export class RsvpSaveParameter {
    AuthToken: string;
    CurrentRole: string;
    AwardID: number;
    WorkLocation: string;
    NominationID: number;
    NomineeAttendance: boolean;
	GuestList: RsvpGuestSaveParameter[];
	SchoolYear: string;
}

export class RsvpGuestSaveParameter {
    GuestNumber: number;
    QuestionList: RsvpQuestionSaveParameter[];
}

export class RsvpQuestionSaveParameter {
    QuestionID: number;
    FormTemplateSectionID: number;
    FormTemplateSectionDetailID: number;
    Response: string;
    OtherResponse: string;
}

// #endregion

// #region Rubric Section Entry Class

export class RubricSectionEntry {
    RubricDetailID: number;
    RubricDetailIDKey: string;
    QuestionText: string;
    SortOrder: number;
    Ratings: RubricSectionRatings[];
}

// #endregion

// #region Rubric Section Ratings Class

export class RubricSectionRatings {
    Text: string;
    Points: number;
    SortOrder: number;
}

// #endregion

// #region Schedule Tie Breaker Result Class

export class ScheduleTieBreakerResult {
    Winners: JudgeNominationScoresInfo[];
    Windows?: NominationWindowInfo[];
}

// #endregion

// #region School Group Info Class

export class SchoolGroupInfo {
    LeadLocationNumber?: string;
    Name?: string;
    SchoolGroupID?: number;
    Schools?: SchoolGroupSchoolInfo[];
    SchoolYear?: string;
}

// #endregion

// #region School Group School Info Class

export class SchoolGroupSchoolInfo {
    SchoolGroupSchoolID?: number;
    LocationNumber?: string;
    LocationName?: string;
    SortOrder?: number;
    IsGroupLeader?: boolean;
    HasNomination?: boolean;
}

// #endregion

// #region School Info Class

export class SchoolInfo {
    SchoolNumber: string;
    SchoolName: string;
    SchoolPhoneNumber: string;
    Principal: string;
    Magnet: boolean;
    Charter: boolean;
    Area: string;
    AreaDescription: string;
    Enr_PK?: boolean;
    Enr_KG?: boolean;
    Enr_01?: boolean;
    Enr_02?: boolean;
    Enr_03?: boolean;
    Enr_04?: boolean;
    Enr_05?: boolean;
    Enr_06?: boolean;
    Enr_07?: boolean;
    Enr_08?: boolean;
    Enr_09?: boolean;
    Enr_10?: boolean;
    Enr_11?: boolean;
    Enr_12?: boolean;
    Enr_30?: boolean;
    Enr_31?: boolean;
    Uses_Elegrade: boolean;
    ExcludeFromEnrCounts: boolean;
    IsElementary?: boolean;
    IsMiddle?: boolean;
    IsSenior?: boolean;
    IsAdult?: boolean;
    DOE_School_Number: string;
    CommunityEligibilityOption: boolean;
    AwardEligibility: number[];

	// These are for school groups
	InSchoolGroup: boolean;
    GroupLeader?: boolean;
	SchoolGroupID?: number;
	SchoolNumberList: string;

    constructor() {
		this.AwardEligibility = [];
		this.InSchoolGroup = false;
	}
}

// #endregion

// #region School Nomination Info Class

export class SchoolNominationInfo {
    AwardID?: number;
    NomineeType: NomineeTypeInfo[];
    Nominees?: NomineeInfo[];
}

// #endregion

// #region School Nominee Info Class

export class SchoolNomineeInfo {
    NomineeType: NomineeTypeInfo;
    NomineeInfo: NomineeInfo;
}

// #endregion

// #region Select Class

export class Select {
    Value: number;
    Display: string;
}

// #endregion

// #region Tie Breaker Window Class

export class TieBreakerWindow {
    BeginDate: Date;
    EndDate: Date;
    AutoAssign: boolean;
}

// #endregion

// #region User Search Result Class

export class UserSearchResult {
    FirstName?: string;
    LastName?: string;
    City?: string;
    Email?: string;
    ActiveDirectoryEmail?: string;
    UserID?: number;
    Username?: string;
    AsAdmin?: boolean;
    SAPID?: string;
    PositionNo?: string;
    LocalJobTitle?: string;
    Password?: string;
}

// #endregion

// #region Window Open Info Class

export class WindowOpenInfo {
    TOYFormWindowOpen: boolean;
    SREFormWindowOpen: boolean;
    TOYPhotoWindowOpen: boolean;
    SREPhotoWindowOpen: boolean;
    TOYVideoWindowOpen: boolean;
    SREVideoWindowOpen: boolean;

    constructor() {
        this.TOYFormWindowOpen = false;
        this.TOYPhotoWindowOpen = false;
        this.TOYVideoWindowOpen = false;
        this.SREFormWindowOpen = false;
        this.SREPhotoWindowOpen = false;
        this.SREVideoWindowOpen = false;
    }
}

// #endregion

// #region Window Type Info Class

export class WindowTypeInfo {
    WindowTypeID: number;
    AwardID: number;
    Code: string;
    Description: string;
    IsAttachment: boolean;
    JudgingPhase?: number;
}

// #endregion

// #endregion

// #region Custom Enumerations

// #region Window Type Enum

export enum WINDOWTYPE {
    NOMINATIONFORM_WINDOW = 1,
    PHOTO_WINDOW = 2,
    VIDEO_WINDOW = 3,
    INITIAL_JUDGING_WINDOW = 4,
    INITIAL_TIEBREAKER_JUDGING_WINDOW = 5,
    FINAL_JUDGING_WINDOW = 6,
    FINAL_TIEBREAKER_JUDGING_WINDOW = 7,
}

// #endregion

// #endregion
